import { 
  collection, 
  doc, 
  getDocs, 
  setDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  Timestamp 
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { LoyaltyProgram, Transaction, UserPreferences } from '../types';

export const userDataService = {
  // Loyalty Programs
  getUserLoyaltyPrograms: async (userId: string): Promise<LoyaltyProgram[]> => {
    try {
      // Simplified query without orderBy to avoid index requirement
      const q = query(
        collection(db, 'userLoyaltyPrograms'),
        where('userId', '==', userId)
      );
      const querySnapshot = await getDocs(q);
      
      const programs = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          expiryDate: data.expiryDate?.toDate(),
          createdAt: data.createdAt?.toDate(),
        } as unknown as LoyaltyProgram;
      });
      
      // Sort manually by createdAt in JavaScript instead of Firestore
      return programs.sort((a, b) => {
        const aDate = a.createdAt || new Date(0);
        const bDate = b.createdAt || new Date(0);
        return bDate.getTime() - aDate.getTime(); // Descending order
      });
    } catch (error) {
      console.error('Error fetching user loyalty programs:', error);
      return [];
    }
  },

  addUserLoyaltyProgram: async (userId: string, program: Omit<LoyaltyProgram, 'id'>): Promise<string> => {
    try {
      const programRef = doc(collection(db, 'userLoyaltyPrograms'));
      const programData = {
        ...program,
        userId,
        expiryDate: program.expiryDate ? Timestamp.fromDate(program.expiryDate) : null,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };
      
      await setDoc(programRef, programData);
      return programRef.id;
    } catch (error) {
      console.error('Error adding loyalty program:', error);
      throw error;
    }
  },

  updateUserLoyaltyProgram: async (programId: string, updates: Partial<LoyaltyProgram>): Promise<void> => {
    try {
      const programRef = doc(db, 'userLoyaltyPrograms', programId);
      const updateData = {
        ...updates,
        expiryDate: updates.expiryDate ? Timestamp.fromDate(updates.expiryDate) : null,
        updatedAt: Timestamp.now(),
      };
      
      await updateDoc(programRef, updateData);
    } catch (error) {
      console.error('Error updating loyalty program:', error);
      throw error;
    }
  },

  deleteUserLoyaltyProgram: async (programId: string): Promise<void> => {
    try {
      await deleteDoc(doc(db, 'userLoyaltyPrograms', programId));
    } catch (error) {
      console.error('Error deleting loyalty program:', error);
      throw error;
    }
  },

  // Transactions
  getUserTransactions: async (userId: string, programId?: string): Promise<Transaction[]> => {
    try {
      let q = query(
        collection(db, 'userTransactions'),
        where('userId', '==', userId),
        orderBy('date', 'desc')
      );

      if (programId) {
        q = query(
          collection(db, 'userTransactions'),
          where('userId', '==', userId),
          where('programId', '==', programId),
          orderBy('date', 'desc')
        );
      }

      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          date: data.date?.toDate() || new Date(),
        } as unknown as Transaction;
      });
    } catch (error) {
      console.error('Error fetching user transactions:', error);
      return [];
    }
  },

  addUserTransaction: async (userId: string, transaction: Omit<Transaction, 'id'>): Promise<string> => {
    try {
      const transactionRef = doc(collection(db, 'userTransactions'));
      const transactionData = {
        ...transaction,
        userId,
        date: Timestamp.fromDate(transaction.date),
        createdAt: Timestamp.now(),
      };
      
      await setDoc(transactionRef, transactionData);
      return transactionRef.id;
    } catch (error) {
      console.error('Error adding transaction:', error);
      throw error;
    }
  },

  // User Preferences
  getUserPreferences: async (userId: string): Promise<UserPreferences> => {
    try {
      const q = query(
        collection(db, 'userPreferences'),
        where('userId', '==', userId)
      );
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const data = querySnapshot.docs[0].data();
        return {
          id: querySnapshot.docs[0].id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
        } as UserPreferences;
      } else {
        // Return default preferences if none exist
        const defaultPrefs: UserPreferences = {
          id: userId,
          userId,
          preferredPrograms: [],
          travelPatterns: {
            domesticVsInternational: 70,
            preferredAirlines: [],
            preferredHotels: [],
            seasonalTravel: [],
            averageTripsPerYear: 4,
            averageSpendPerTrip: 2000
          },
          redemptionGoals: {
            shortTerm: [],
            longTerm: [],
            targetValue: 1.2
          },
          notifications: {
            expiryAlerts: true,
            expiryDaysThreshold: 60,
            transferDeals: true,
            redemptionSuggestions: true,
            weeklyReports: false,
            emailNotifications: true
          },
          privacy: {
            shareAnonymousData: false,
            trackingConsent: true
          },
          createdAt: new Date(),
          updatedAt: new Date()
        };
        
        // Save default preferences
        await setDoc(doc(db, 'userPreferences', userId), {
          ...defaultPrefs,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now()
        });
        
        return defaultPrefs;
      }
    } catch (error) {
      console.error('Error fetching user preferences:', error);
      throw error;
    }
  },

  updateUserPreferences: async (userId: string, updates: Partial<UserPreferences>): Promise<void> => {
    try {
      const prefRef = doc(db, 'userPreferences', userId);
      const updateData = {
        ...updates,
        updatedAt: Timestamp.now()
      };
      
      await updateDoc(prefRef, updateData);
    } catch (error) {
      console.error('Error updating user preferences:', error);
      throw error;
    }
  },

  // Dashboard Analytics
  getUserDashboardData: async (userId: string) => {
    try {
      const [programs, transactions] = await Promise.all([
        userDataService.getUserLoyaltyPrograms(userId),
        userDataService.getUserTransactions(userId)
      ]);

      const totalPoints = programs.reduce((sum, program) => {
        return sum + (program.points || 0);
      }, 0);

      const totalValue = programs.reduce((sum, program) => {
        return sum + ((program.points || 0) * (program.conversionRate || 1) * 0.01);
      }, 0);

      const pointsExpiringSoon = programs
        .filter(program => {
          if (!program.expiryDate) return false;
          const daysUntilExpiry = Math.ceil((program.expiryDate.getTime() - new Date().getTime()) / (1000 * 3600 * 24));
          return daysUntilExpiry <= 30 && daysUntilExpiry > 0;
        })
        .reduce((sum, program) => sum + (program.points || 0), 0);

      const recentTransactions = transactions.slice(0, 10);

      return {
        totalPoints,
        totalValue: Math.round(totalValue),
        pointsExpiringSoon,
        recentTransactions,
        programs,
      };
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      return {
        totalPoints: 0,
        totalValue: 0,
        pointsExpiringSoon: 0,
        recentTransactions: [],
        programs: [],
      };
    }
  }
};